/**
 * Asset Loader for 3D models and textures
 */

import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { SVGLoader } from "three/examples/jsm/loaders/SVGLoader";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { eMatType, MODEL_MAT } from "../constants/materials";
import MatUtils from "./MatUtils";
import { Ao } from "@/services";

export class Loader {
  private static instance: Loader | null = null;

  gltfLoader!: GLTFLoader;
  dracoLoader!: DRACOLoader;
  objectLoader!: THREE.ObjectLoader;
  fbxLoader!: FBXLoader;
  textureLoader!: THREE.TextureLoader;
  objLoader!: OBJLoader;
  svgLoader!: SVGLoader;
  stlLoader!: STLLoader;
  loadedDict: Record<string, any> = {};

  // Dependencies
  private ao?: Ao;

  private constructor() {
    this.initializeLoaders();
  }

  // Singleton getInstance method
  static getInstance(): Loader {
    if (!Loader.instance) {
      Loader.instance = new Loader();
    }
    return Loader.instance;
  }

  // Method to set dependencies after initialization
  setDependencies(ao?: any): void {
    this.ao = ao;
  }

  // Check if dependencies are set
  hasDependencies(): boolean {
    return !!this.ao;
  }

  // Reset singleton instance (useful for testing or cleanup)
  static resetInstance(): void {
    if (Loader.instance) {
      Loader.instance.clearCache();
      Loader.instance = null;
    }
  }

  private initializeLoaders(): void {
    // Initialize DRACO loader for compressed geometry
    this.dracoLoader = new DRACOLoader();
    this.dracoLoader.setDecoderPath(
      "https://threejs.org/examples/jsm/libs/draco/"
    );
    this.dracoLoader.preload();

    // Initialize other loaders
    this.fbxLoader = new FBXLoader();
    this.gltfLoader = new GLTFLoader();
    this.gltfLoader.setDRACOLoader(this.dracoLoader);
    this.objectLoader = new THREE.ObjectLoader();
    this.textureLoader = new THREE.TextureLoader();
    this.objLoader = new OBJLoader();
    this.svgLoader = new SVGLoader();
    this.stlLoader = new STLLoader();
  }

  /**
   * Load GLTF/GLB model
   */
  loadGLTF = async (
    src: string,
    name: string,
    isReceiveShadow = false,
    preserveOriginalMaterials: boolean = false,
    mat = MODEL_MAT
  ) => {
    if (!this.loadedDict[src]) {
      const object = await this.gltfLoader.loadAsync(src);
      this.loadedDict[src] = object.scene.children[0];
    }

    const result = this.loadedDict[src].clone();
    if (name) {
      result.name = name;
      result.userData.name = name;
    }
    if (!preserveOriginalMaterials) {
      const promises: Promise<void>[] = [];
      result.traverse(async (child: THREE.Object3D) => {
        promises.push(
          this.adjustMesh(
            true,
            child,
            name,
            mat,
            eMatType.STANDARD,
            true,
            isReceiveShadow,
            {},
            null
          )
        );
      });
      await Promise.all(promises);
    }
    return result;
  };

  loadFBX = async (
    src: string,
    name: string,
    isReceiveShadow = false,
    mat = MODEL_MAT
  ) => {
    try {
      if (!this.loadedDict[src]) {
        const gltf = await this.fbxLoader.loadAsync(src);
        this.loadedDict[src] = gltf;
      }

      const object = this.loadedDict[src].clone();
      object.name = name;

      const promises: Promise<void>[] = [];
      object.traverse(async (child: THREE.Object3D) => {
        promises.push(
          this.adjustMesh(
            true,
            child,
            name,
            mat,
            eMatType.STANDARD,
            true,
            isReceiveShadow,
            {},
            null
          )
        );
      });
      await Promise.all(promises);
      return object;
    } catch (error) {
      console.error(
        `Loader: Failed to load FBX ${src} with name ${name}:`,
        error
      );
      throw error;
    }
  };

  loadObj = async (
    src: string,
    name: string,
    matType: number,
    mat: any,
    maps: any,
    material: any = null,
    ao: boolean = false
  ) => {
    try {
      if (!this.loadedDict[src]) {
        const obj = await this.objLoader.loadAsync(src);
        this.loadedDict[src] = obj;
      }

      const object = this.loadedDict[src].clone();
      object.name = name;

      const promises: Promise<void>[] = [];
      object.traverse(async (child: THREE.Object3D) => {
        if (this.ao?.materialSwitcher && ao) {
          promises.push(
            this.adjustMesh(
              true,
              child,
              name,
              mat,
              matType,
              false,
              true,
              maps,
              material
            )
          );
        } else {
          promises.push(
            this.adjustMesh(
              false,
              child,
              name,
              mat,
              matType,
              false,
              true,
              maps,
              material
            )
          );
        }
      });
      await Promise.all(promises);
      return object;
    } catch (error) {
      console.error(
        `Loader: Failed to load OBJ ${src} with name ${name}:`,
        error
      );
      throw error;
    }
  };

  /**
   * Load STL model
   */
  async loadStl(
    src: string,
    name: string,
    material?: THREE.Material
  ): Promise<THREE.Mesh> {
    try {
      if (!this.loadedDict[src]) {
        const geometry = await this.stlLoader.loadAsync(src);
        const mesh = new THREE.Mesh(geometry);
        this.loadedDict[src] = mesh;
      }

      const object = this.loadedDict[src].clone();
      object.name = name;

      if (object instanceof THREE.Mesh) {
        await this.adjustMesh(
          false,
          object,
          name,
          {},
          eMatType.STANDARD,
          true,
          true,
          {},
          material
        );
      }

      return object;
    } catch (error) {
      console.error("Failed to load STL:", error);
      throw error;
    }
  }

  /**
   * Load STL from buffer
   */
  async loadStlFromBuffer(
    arrayBuffer: ArrayBuffer,
    name: string,
    material?: THREE.Material
  ): Promise<THREE.Mesh> {
    try {
      const geometry = this.stlLoader.parse(arrayBuffer);
      const mesh = new THREE.Mesh(geometry);
      mesh.name = name;

      await this.adjustMesh(
        false,
        mesh,
        name,
        {},
        eMatType.STANDARD,
        true,
        true,
        {},
        material
      );
      return mesh;
    } catch (error) {
      console.error("Failed to load STL from buffer:", error);
      throw error;
    }
  }

  /**
   * Load texture
   */
  async loadTexture(src: string): Promise<THREE.Texture> {
    try {
      return await this.textureLoader.loadAsync(src);
    } catch (error) {
      console.error("Failed to load texture:", error);
      throw error;
    }
  }

  /**
   * Parse JSON object
   */
  async parseJSON(src: any, name: string): Promise<THREE.Object3D> {
    try {
      return new Promise((resolve, reject) => {
        this.objectLoader.parse(src, (object) => {
          object.name = name;
          resolve(object);
        });
      });
    } catch (error) {
      console.error("Failed to parse JSON:", error);
      throw error;
    }
  }

  /**
   * Adjust mesh properties and materials
   */
  adjustMesh = async (
    ao: boolean,
    child: THREE.Object3D,
    assetName: string,
    mat: any,
    matType: number,
    castShadow: boolean,
    receiveShadow: boolean,
    maps: any = {},
    material: any
  ) => {
    child.castShadow = castShadow;
    child.receiveShadow = receiveShadow;
    child.frustumCulled = false;

    // fix for https://github.com/mrdoob/three.js/pull/16639
    if (child.name !== child.userData.name) {
      child.name = child.userData.name;
    }

    if (child instanceof THREE.Mesh) {
      child.userData.assetName = assetName;
      child.material = await MatUtils.getMat(
        matType,
        mat,
        maps,
        material,
        assetName ? assetName.includes("paralax") : false
      );

      if (ao) {
        child.receiveShadow = false;
        if (this.ao?.materialSwitcher) {
          this.ao.setMaterialCorrespondance(child);
        }
      }
    }
  };

  /**
   * Clear loaded assets cache
   */
  clearCache(): void {
    this.loadedDict = {};
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { count: number; keys: string[] } {
    return {
      count: Object.keys(this.loadedDict).length,
      keys: Object.keys(this.loadedDict)
    };
  }
}

export const loader = Loader.getInstance();
